import { useState } from "react";
import Seo from "../components/Seo";
import CaptchaWidget from "../components/CaptchaWidget";
import { hasErrors, validateContactForm } from "../utils/validation";
import api, { ApiError } from "../utils/api";

const INITIAL_VALUES = { name: "", email: "", message: "", website: "" };

export default function Contact() {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  function updateField(field, value) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);

    if (values.website) return; // honeypot triggered; drop silently

    const validationErrors = validateContactForm(values);
    if (!captchaToken) validationErrors.captcha = "Please complete the verification check.";
    setErrors(validationErrors);
    if (hasErrors(validationErrors)) return;

    setSubmitting(true);
    try {
      await api.submitContact({ ...values, captchaToken });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : "Unable to send your message. Please try again.");
    } finally {
      setCaptchaToken("");
      setCaptchaReset((value) => value + 1);
      setSubmitting(false);
    }
  }

  return (
    <>
      <Seo title="Contact" description="Contact the HS Library Hangout organizers." />
      <h1>Contact us</h1>
      <p>
        Have a question about an event, accessibility needs, or anything else? Send us a message below.
        This form goes directly to the organizer &mdash; not to Oak Lawn Public Library.
      </p>

      {submitted ? (
        <div className="alert alert-success" role="status">
          Thanks for reaching out! We&rsquo;ll get back to you soon.
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="card">
          {submitError && (
            <div className="alert alert-error" role="alert">
              {submitError}
            </div>
          )}
          <div className="form-field">
            <label htmlFor="contact-name">Your name *</label>
            <input
              id="contact-name"
              type="text"
              required
              value={values.name}
              onChange={(e) => updateField("name", e.target.value)}
              aria-invalid={Boolean(errors.name)}
            />
            {errors.name && <p className="field-error">{errors.name}</p>}
          </div>
          <div className="form-field">
            <label htmlFor="contact-email">Your email *</label>
            <input
              id="contact-email"
              type="email"
              required
              value={values.email}
              onChange={(e) => updateField("email", e.target.value)}
              aria-invalid={Boolean(errors.email)}
            />
            {errors.email && <p className="field-error">{errors.email}</p>}
          </div>
          <div className="form-field">
            <label htmlFor="contact-message">Message *</label>
            <textarea
              id="contact-message"
              required
              value={values.message}
              onChange={(e) => updateField("message", e.target.value)}
              aria-invalid={Boolean(errors.message)}
            />
            {errors.message && <p className="field-error">{errors.message}</p>}
          </div>

          <div className="honeypot-field" aria-hidden="true">
            <label htmlFor="contact-website">Leave this field blank</label>
            <input
              id="contact-website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={values.website}
              onChange={(e) => updateField("website", e.target.value)}
            />
          </div>

          <div className="form-field">
            <CaptchaWidget onToken={setCaptchaToken} resetKey={captchaReset} />
            {errors.captcha && <p className="field-error">{errors.captcha}</p>}
          </div>

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Sending\u2026" : "Send message"}
          </button>
        </form>
      )}
    </>
  );
}
